import { useEffect, useRef, useState } from "react";
import type { PublicPcConfig } from "../scenario/types";
import { audio, duckSceneAudio } from "../lib/audio";

type Stage =
  | "off"
  | "desktop"
  | "browser"
  | "login"
  | "inbox"
  | "leaving"
  | "exposed"
  | "safe_result";

export type PublicPcResult = {
  safe: boolean;
  eventId: string;
  outcome: string;
  label: string;
};

type FilmStage = "off" | "desktop" | "browser" | "login" | "page" | "playing" | "ended";

/**
 * The same business-centre PC in film mode (`config.film`): power on, open the
 * Browser, sign in to the awareness portal, then watch the film or skip it.
 * Reports stages as `film_<stage>` so App's PC_STAGE_GUIDE can follow along.
 */
export function PublicPcFilm({
  config,
  onDone,
  onStageChange,
}: {
  config: PublicPcConfig;
  onDone: () => void;
  onStageChange?: (stage: string) => void;
}) {
  const film = config.film!;
  const [stage, setStageRaw] = useState<FilmStage>("off");
  const [typed, setTyped] = useState("");
  const vidRef = useRef<HTMLVideoElement>(null);
  const ducked = useRef(false);
  const portal = film.portalUrl ?? "learn.corp-acme.com";
  const playLabel = film.playLabel ?? "Watch the video";
  const skipLabel = film.skipLabel ?? "Skip the video";
  const continueLabel = film.continueLabel ?? "Continue to quiz";

  const setStage = (s: FilmStage) => {
    setStageRaw(s);
    onStageChange?.(`film_${s}`);
  };
  const unduck = () => {
    if (ducked.current) {
      ducked.current = false;
      duckSceneAudio(1);
    }
  };

  useEffect(() => {
    onStageChange?.("film_off");
    return () => {
      vidRef.current?.pause();
      unduck();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBrowser = () => {
    audio.click();
    setStage("browser");
    setTyped("");
    let i = 0;
    const iv = window.setInterval(() => {
      i += 1;
      setTyped(portal.slice(0, i));
      if (i >= portal.length) {
        window.clearInterval(iv);
        window.setTimeout(() => setStage("login"), 500);
      }
    }, 55);
  };

  const play = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = !audio.isEnabled();
    ducked.current = true;
    duckSceneAudio(0.2);
    void v.play();
    setStage("playing");
  };

  const finish = () => {
    vidRef.current?.pause();
    unduck();
    onDone();
  };

  if (stage === "off") {
    return (
      <button
        type="button"
        className="screen-power pc-power cf-pulse-target"
        onClick={() => {
          audio.powerOn();
          setStage("desktop");
        }}
      >
        <span className="screen-power-ico" aria-hidden>
          <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M12 2v9" />
            <path d="M6.5 5.5a8 8 0 1 0 11 0" />
          </svg>
        </span>
        <span className="screen-power-label">Click to power on the PC</span>
      </button>
    );
  }

  return (
    <div className="pc-wrap" aria-label="Business centre PC">
      <div className="pc-monitor">
        {stage === "desktop" && (
          <div className="pc-desktop">
            <div className="pc-desktop-brand">{film.desktopBrand ?? "Business Centre Guest PC"}</div>
            <button type="button" className="pc-icon cf-pulse-target" onClick={openBrowser}>
              <span className="pc-icon-glyph" aria-hidden>{"\u{1F310}"}</span>
              <span>Browser</span>
              <span className="pc-hint-chip">Click to open</span>
            </button>
            <div className="pc-taskbar" aria-hidden />
          </div>
        )}

        {stage !== "desktop" && (
          <div className="pc-browser">
            <div className="pc-browser-bar">
              <span className="pc-browser-lock" aria-hidden>{"\u{1F512}"}</span>
              <span className="pc-browser-url">{stage === "browser" ? typed : portal}</span>
            </div>

            {stage === "browser" && <div className="pc-browser-page pc-blank" />}

            {stage === "login" && (
              <div className="pc-browser-page pc-login">
                <div className="pc-login-card">
                  <div className="pc-login-brand">{film.portalBrand ?? "Security awareness portal"}</div>
                  <label>
                    <span>Work email</span>
                    <input readOnly value="max.berger@corp-acme.com" />
                  </label>
                  <label>
                    <span>Password</span>
                    <input readOnly type="text" value="••••••••••" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                  </label>
                  <button type="button" className="btn cf-pulse-target" onClick={() => { audio.click(); setStage("page"); }}>
                    Log in
                  </button>
                </div>
              </div>
            )}

            {(stage === "page" || stage === "playing" || stage === "ended") && (
              <div className="pc-browser-page pc-film">
                <video
                  ref={vidRef}
                  className={stage === "page" ? "pc-film-vid wait" : "pc-film-vid"}
                  src={film.url}
                  preload="metadata"
                  playsInline
                  controls={stage === "playing"}
                  onEnded={() => {
                    unduck();
                    setStage("ended");
                  }}
                />
                {stage === "page" && (
                  <div className="pc-film-intro">
                    <div className="pc-film-kicker">{film.brand ?? "Security awareness"}</div>
                    <h2>{film.headline}</h2>
                    <p>{film.body}</p>
                    <div className="pc-film-actions">
                      <button type="button" className="btn cf-pulse-target" onClick={play}>
                        {"\u25B6"} {playLabel}
                      </button>
                      <button type="button" className="btn btn-ghost" onClick={finish}>
                        {skipLabel}
                      </button>
                    </div>
                  </div>
                )}
                {stage === "playing" && (
                  <button type="button" className="btn btn-ghost pc-film-skip" onClick={finish}>
                    {skipLabel}
                  </button>
                )}
                {stage === "ended" && (
                  <div className="pc-film-end">
                    <span>The film has finished.</span>
                    <button type="button" className="btn cf-pulse-target" onClick={finish}>
                      {continueLabel}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Hotel business-centre public PC. Max's phone is low again, so he checks
 * work email on the shared guest PC: power on -> open the browser -> type the
 * company webmail address -> log in -> read mail -> then an EXPLICIT decision
 * ("Just close the tab" risky vs "Sign out first" safe), same as every other
 * decision point in this course — never a tiny disguised chrome button.
 * "Just close the tab" shows the NEXT guest reopening the browser and landing
 * back in the still-signed-in inbox via session + history.
 */
export function PublicPc({
  config,
  onComplete,
  onStageChange,
}: {
  config: PublicPcConfig;
  onComplete: (result: PublicPcResult) => void;
  onStageChange?: (stage: Stage) => void;
}) {
  const [stage, setStageRaw] = useState<Stage>("off");
  const [typed, setTyped] = useState("");
  const spoke = useRef(false);
  const emailUrl = config.emailUrl ?? "mail.corp-acme.com";

  const setStage = (s: Stage) => {
    setStageRaw(s);
    onStageChange?.(s);
  };

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    void audio.prefetchSpeech(
      config.body ??
        "Max checks his work email on the business centre PC. He reads two messages, then his shuttle is called.",
      { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
    );
    if (config.lenaRisky) void audio.prefetchSpeech(config.lenaRisky, { voice: "nova", lang: config.language ?? "en-US" });
    if (config.lenaSafe) void audio.prefetchSpeech(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    onStageChange?.("off");
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const powerOn = () => {
    audio.powerOn();
    setStage("desktop");
  };

  const openBrowser = () => {
    audio.click();
    setStage("browser");
    setTyped("");
    let i = 0;
    const iv = window.setInterval(() => {
      i += 1;
      setTyped(emailUrl.slice(0, i));
      if (i >= emailUrl.length) {
        window.clearInterval(iv);
        window.setTimeout(() => setStage("login"), 500);
      }
    }, 55);
  };

  const login = () => {
    audio.click();
    setStage("inbox");
    if (!spoke.current) {
      spoke.current = true;
      void audio.speak(
        config.body ??
          "Max checks his work email on the business centre PC. He reads two messages, then his shuttle is called.",
        { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
      );
    }
  };

  const [pendingResult, setPendingResult] = useState<PublicPcResult | null>(null);

  const justClose = () => {
    const result: PublicPcResult = {
      safe: false,
      eventId: "pc_session_left_open",
      outcome: "risky",
      label: "Closed the browser tab without signing out",
    };
    setPendingResult(result);
    setStage("leaving");
    if (config.lenaRisky) void audio.speak(config.lenaRisky, { voice: "nova", lang: config.language ?? "en-US" });
    window.setTimeout(() => setStage("exposed"), 1400);
  };

  const signOutThenClose = () => {
    const result: PublicPcResult = {
      safe: true,
      eventId: "pc_session_signed_out",
      outcome: "safe",
      label: "Signed out, then closed the browser",
    };
    setPendingResult(result);
    if (config.lenaSafe) void audio.speak(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    setStage("safe_result");
  };

  if (stage === "off") {
    return (
      <button type="button" className="screen-power pc-power cf-pulse-target" onClick={powerOn}>
        <span className="screen-power-ico" aria-hidden>
          <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M12 2v9" />
            <path d="M6.5 5.5a8 8 0 1 0 11 0" />
          </svg>
        </span>
        <span className="screen-power-label">Click to power on the PC</span>
      </button>
    );
  }

  return (
    <div className="pc-wrap" aria-label="Business centre PC">
      <div className="pc-monitor">
        {stage === "desktop" && (
          <div className="pc-desktop">
            <div className="pc-desktop-brand">Business Centre Guest PC</div>
            <button type="button" className="pc-icon cf-pulse-target" onClick={openBrowser}>
              <span className="pc-icon-glyph" aria-hidden>{"\u{1F310}"}</span>
              <span>Browser</span>
              <span className="pc-hint-chip">Click to open</span>
            </button>
            <div className="pc-taskbar" aria-hidden />
          </div>
        )}

        {(stage === "browser" || stage === "login" || stage === "inbox") && (
          <div className="pc-browser">
            <div className="pc-browser-bar">
              <span className="pc-browser-lock" aria-hidden>{"\u{1F512}"}</span>
              <span className="pc-browser-url">{stage === "browser" ? typed : emailUrl}</span>
            </div>

            {stage === "browser" && <div className="pc-browser-page pc-blank" />}

            {stage === "login" && (
              <div className="pc-browser-page pc-login">
                <div className="pc-login-card">
                  <div className="pc-login-brand">Corp Webmail</div>
                  <label>
                    <span>Work email</span>
                    <input readOnly value="max.berger@corp-acme.com" />
                  </label>
                  <label>
                    <span>Password</span>
                    <input readOnly type="text" value="••••••••••" autoComplete="off" data-lpignore="true" data-1p-ignore="true" />
                  </label>
                  <button type="button" className="btn cf-pulse-target" onClick={login}>
                    Log in
                  </button>
                </div>
              </div>
            )}

            {stage === "inbox" && (
              <div className="pc-browser-page pc-inbox">
                <div className="pc-inbox-list">
                  <div className="pc-inbox-row unread">
                    <strong>Nina Keller</strong>
                    <span>Updated pricing sheet attached</span>
                  </div>
                </div>
                <div className="pc-decide">
                  <p className="pc-decide-q">Shuttle leaves in 5 min. How does Max leave this PC?</p>
                  <div className="pc-decide-actions">
                    <button type="button" className="pc-decide-btn cf-pulse-target" onClick={justClose}>
                      Just close the browser tab
                    </button>
                    <button type="button" className="pc-decide-btn" onClick={signOutThenClose}>
                      Sign out, then close the tab
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {stage === "leaving" && (
          <div className="pc-browser-page pc-closing">
            <span aria-hidden>{"\u2715"}</span>
            Browser tab closed.
          </div>
        )}

        {stage === "exposed" && pendingResult && (
          // Copy trimmed 2026-07-13 to fit the fixed unscaled monitor box
          // without scrolling -- see the compaction note on .pc-exposed in
          // styles.css. Same two teaching points, much shorter.
          <div className="pc-exposed">
            <div className="pc-exposed-badge">30 min later — next guest</div>
            <h2>The inbox is still open</h2>
            <p>The browser restores Max&apos;s last session — still signed in, no password needed.</p>
            <ul className="pc-exposed-list">
              <li>
                <strong>The session never expired.</strong> Closing a tab isn&apos;t signing out — only
                &quot;Sign out&quot; (or a timeout) ends it.
              </li>
              <li>
                <strong>History saved the address too</strong> — anyone next can just click{" "}
                <code>mail.corp-acme.com</code>.
              </li>
            </ul>
            <button type="button" className="btn cf-pulse-target" onClick={() => onComplete(pendingResult)}>
              Continue
            </button>
          </div>
        )}

        {stage === "safe_result" && pendingResult && (
          <div className="pc-exposed pc-exposed--safe">
            <div className="pc-exposed-badge good">Signed out</div>
            <h2>Session properly ended</h2>
            <p>Signing out invalidates the session server-side — the next guest gets a blank login page.</p>
            <button type="button" className="btn cf-pulse-target" onClick={() => onComplete(pendingResult)}>
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
